

import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { cookies } from '@/lib/stubs';
import crypto from 'crypto';
import http from 'http';
import https from 'https';

const DEV = process.env.NODE_ENV === 'development';
const TAG = '[SERVER][ApiClient]';
const CLIENT_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? '1.0.0';

/* =============================================================================
 * CONFIGURATION CONSTANTS
 * ============================================================================= */

const ENV = {
  isDevelopment: DEV,
  isProduction: process.env.NODE_ENV === 'production',
  isTest: process.env.NODE_ENV === 'test',

  defaultTimeout: DEV ? 45_000 : 30_000,
  authRegisterTimeout: DEV ? 45_000 : 35_000,
  authRefreshTimeout: DEV ? 30_000 : 25_000,
  vendorRoutesTimeout: DEV ? 30_000 : 25_000,

  maxRetries: DEV ? 2 : 1,
  retryDelayBase: 300,
  retryJitter: 100,

  maxSockets: parseInt(process.env.NEXT_PUBLIC_API_MAX_SOCKETS || '64', 10),
  socketTimeout: DEV ? 65_000 : 45_000,

  circuitBreakerThreshold: 5,
  circuitBreakerResetTimeout: 30_000,
} as const;

/* =============================================================================
 * TYPES
 * ============================================================================= */

export interface ApiSuccessResponse<T = unknown> {
  status: 'success';
  message?: string;
  data: T;
  timestamp?: string;
  requestId?: string;
}

export type ApiErrorResponse = {
  status: 'error';
  message: string;
  errors?: Array<{ field?: string; message?: string; code?: string }>;
  timestamp?: string;
  data: null;
  requestId?: string;
};

export interface RequestMetaConfig {
  /**
   * Default: true
   * - true  => attempt to attach Authorization (if cookie exists) and treat 401 as auth-expired
   * - false => never attach Authorization and never treat 401 as auth-expired
   */
  requiresAuth?: boolean;
  /**
   * Optional override. If you want to set a specific request id from upstream.
   */
  requestId?: string;
  /**
   * Optional: extra context for logs/telemetry
   */
  label?: string;
}

export interface StandardizedError {
  message: string;
  code?: string;
  status?: number;
  requestId: string;
  url?: string;
  method?: string;
  timestamp: string;

  isTransient: boolean;
  isNetworkError: boolean;
  isTimeout: boolean;
  isServerError: boolean;
  isClientError: boolean;

  /**
   * True only when:
   * - requiresAuth = true
   * - status = 401 or 419
   */
  isAuthExpired?: boolean;

  attempt?: number;
  durationMs?: number;
  stack?: string;

  raw?: unknown;
}

interface RetryConfig {
  maxRetries: number;
  retryableMethods: Set<string>;
  retryDelay: (attempt: number) => number;
  shouldRetry: (error: StandardizedError, attempt: number) => boolean;
}

interface RequestMetaInternal {
  requestId: string;
  startedAt: number;
  method: string;
  url: string;
  timeout: number;
  attempt: number;
  routeCategory?: string;
  endpoint?: string;

  requiresAuth: boolean;

  label?: string;
}

interface CircuitBreakerState {
  failures: number;
  lastFailure: number;
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
}

/* =============================================================================
 * REFRESH LOCK (SINGLE-FLIGHT PATTERN)
 * ============================================================================= */

class RefreshLockManager {
  private static instance: RefreshLockManager;
  private refreshInProgress: boolean = false;
  private waitingPromises: Array<{
    resolve: (token: string | null) => void;
    reject: (error: Error) => void;
  }> = [];
  private lastRefreshTime: number = 0;
  private readonly refreshCooldownMs = 5000; // 5 second cooldown

  static getInstance(): RefreshLockManager {
    if (!RefreshLockManager.instance) {
      RefreshLockManager.instance = new RefreshLockManager();
    }
    return RefreshLockManager.instance;
  }

  /**
   * Attempt to acquire refresh lock. Returns:
   * - Promise<string | null> if this caller should perform refresh
   * - Promise that resolves when refresh completes if caller should wait
   */
  async acquireLock(): Promise<string | null> {
    const now = Date.now();
    
    // Check cooldown period
    if (now - this.lastRefreshTime < this.refreshCooldownMs) {
      return null; // Too soon to refresh again
    }

    if (this.refreshInProgress) {
      // Wait for the current refresh to complete
      return new Promise((resolve, reject) => {
        this.waitingPromises.push({ resolve, reject });
      });
    }

    // Acquire the lock
    this.refreshInProgress = true;
    this.lastRefreshTime = now;
    return null; // Caller should perform refresh
  }

  /**
   * Release the lock and notify all waiting promises
   */
  releaseLock(result: { token: string | null; error?: Error }): void {
    this.refreshInProgress = false;
    
    // Notify all waiting promises
    this.waitingPromises.forEach(({ resolve, reject }) => {
      if (result.error) {
        reject(result.error);
      } else {
        resolve(result.token);
      }
    });
    
    // Clear the waiting queue
    this.waitingPromises = [];
  }

  /**
   * Force release lock (in case of errors)
   */
  forceRelease(): void {
    this.refreshInProgress = false;
    
    // Reject all waiting promises
    this.waitingPromises.forEach(({ reject }) => {
      reject(new Error('Refresh was forcefully cancelled'));
    });
    
    this.waitingPromises = [];
  }
}

/* =============================================================================
 * AXIOS CONFIG AUGMENTATION (meta support)
 * ============================================================================= */

declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface AxiosRequestConfig {
    meta?: RequestMetaConfig;
  }
}

/* =============================================================================
 * UTILITIES
 * ============================================================================= */

class RequestIdGenerator {
  static generate(): string {
    try {
      return crypto.randomUUID();
    } catch {
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).substring(2, 10);
      return `req_${timestamp}_${random}`;
    }
  }
}

class Timer {
  private startTime: number;
  constructor() {
    this.startTime = performance.now();
  }
  elapsedMs(): number {
    return Math.round(performance.now() - this.startTime);
  }
}

function safeJson(payload: unknown) {
  try {
    return payload ? JSON.parse(JSON.stringify(payload)) : undefined;
  } catch {
    return undefined;
  }
}

function devLog(
  level: 'debug' | 'info' | 'warn' | 'error',
  msg: string,
  payload?: Record<string, unknown>
) {
  if (!DEV) return;
  const fn = console[level] ?? console.log;
  fn(`${TAG} ${msg}`, payload ? safeJson(payload) : '');
}

/* =============================================================================
 * ROUTE CLASSIFIER (kept for timeout/category, NOT auth)
 * ============================================================================= */

class RouteClassifier {
  private static routePatterns = {
    register: /^\/auth\/register\/?$/i,
    login: /^\/auth\/login\/?$/i,
    refresh: /^\/auth\/refresh(-token)?\/?$/i,
    me: /^\/auth\/me\/?$/i,

    myVendors: /^\/my-vendors\/?(\?.*)?$/i,
    vendorById: /^\/vendors\/[^\/]+\/?$/i,
    vendorOnboarding: /^\/vendors\/onboarding\/?/i,

    upload: /\/upload\/?/i,
    export: /\/export\/?/i,
    report: /\/reports\/?/i,
  } as const;

  static classify(url?: string, method?: string): {
    category: string;
    isIdempotent: boolean;
    isLongRunning: boolean;
    timeout: number;
  } {
    const path = url?.split('?')[0] || '';
    const httpMethod = (method || 'GET').toUpperCase();

    const classification = {
      category: 'generic',
      isIdempotent: httpMethod === 'GET' || httpMethod === 'HEAD' || httpMethod === 'OPTIONS',
      isLongRunning: false,
      timeout: ENV.defaultTimeout,
    };

    if (this.routePatterns.register.test(path)) {
      return { ...classification, category: 'auth.register', isIdempotent: false, timeout: ENV.authRegisterTimeout };
    }
    if (this.routePatterns.login.test(path)) {
      return { ...classification, category: 'auth.login', isIdempotent: false, timeout: ENV.defaultTimeout };
    }
    if (this.routePatterns.refresh.test(path)) {
      return { ...classification, category: 'auth.refresh', isIdempotent: false, timeout: ENV.authRefreshTimeout };
    }
    if (this.routePatterns.me.test(path)) {
      return { ...classification, category: 'auth.me', isIdempotent: false, timeout: ENV.defaultTimeout };
    }
    if (
      this.routePatterns.myVendors.test(path) ||
      this.routePatterns.vendorById.test(path) ||
      this.routePatterns.vendorOnboarding.test(path)
    ) {
      return { ...classification, category: 'vendor', timeout: ENV.vendorRoutesTimeout };
    }
    if (this.routePatterns.upload.test(path) || this.routePatterns.export.test(path) || this.routePatterns.report.test(path)) {
      return {
        ...classification,
        category: 'long_running',
        isIdempotent: false,
        isLongRunning: true,
        timeout: ENV.defaultTimeout * 2,
      };
    }

    return classification;
  }
}

/* =============================================================================
 * ERROR NORMALIZER (improved for transient errors)
 * ============================================================================= */

class ErrorNormalizer {
  static normalize(
    error: unknown,
    meta?: Partial<RequestMetaInternal>
  ): StandardizedError {
    const requestId = meta?.requestId || RequestIdGenerator.generate();
    const timestamp = new Date().toISOString();

    const base: StandardizedError = {
      message: 'An unexpected error occurred',
      requestId,
      timestamp,
      isTransient: false,
      isNetworkError: false,
      isTimeout: false,
      isServerError: false,
      isClientError: false,
      url: meta?.url,
      method: meta?.method,
      attempt: meta?.attempt,
    };

    if (axios.isAxiosError(error)) {
      return this.normalizeAxiosError(error, base, meta);
    }

    if (error instanceof Error) {
      return { ...base, message: error.message, code: 'UNKNOWN_ERROR', stack: error.stack, raw: error };
    }

    if (typeof error === 'string') {
      return { ...base, message: error, code: 'STRING_ERROR', raw: error };
    }

    return { ...base, message: String(error), code: 'UNKNOWN', raw: error };
  }

  static normalizeHttpResponseAsError(
    response: AxiosResponse,
    meta: RequestMetaInternal
  ): StandardizedError {
    const status = response.status;
    const timestamp = new Date().toISOString();
    const isServerError = status >= 500 && status < 600;
    const isClientError = status >= 400 && status < 500;

    // CRITICAL FIX: Only mark auth expired for 401/419 when requiresAuth = true
    const isAuthExpired = Boolean(
      meta.requiresAuth && 
      (status === 401 || status === 419)
    );

    const message =
      isAuthExpired
        ? 'Session expired. Please log in again.'
        : status === 403
          ? 'Forbidden.'
          : status === 404
            ? 'Resource not found.'
            : status === 429
              ? 'Too many requests. Please try again later.'
              : isServerError
                ? 'Server error. Please try again later.'
                : `Request failed with HTTP ${status}.`;

    const code = isAuthExpired ? 'AUTH_EXPIRED' : `HTTP_${status}`;

    // Transient errors: timeouts, rate limits, server errors
    const isTransient = this.isTransientError(status, undefined, undefined);

    return {
      message,
      code,
      status,
      requestId: meta.requestId,
      timestamp,
      url: meta.url,
      method: meta.method,
      attempt: meta.attempt,
      durationMs: meta.startedAt ? Date.now() - meta.startedAt : undefined,

      isTransient,
      isNetworkError: false,
      isTimeout: status === 408,
      isServerError,
      isClientError,

      isAuthExpired,

      raw: {
        response: {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
          headers: response.headers,
        },
      },
    };
  }

  private static normalizeAxiosError(
    error: AxiosError,
    base: StandardizedError,
    meta?: Partial<RequestMetaInternal>
  ): StandardizedError {
    const status = error.response?.status;
    const code = (error.code as string) || 'AXIOS_ERROR';
    const message = error.message || 'Network request failed';

    const isNetworkError = !error.response && !!error.request;
    const isTimeout = code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timeout/i.test(message);
    const isServerError = status ? status >= 500 && status < 600 : false;
    const isClientError = status ? status >= 400 && status < 500 : false;

    const isTransient = this.isTransientError(status, code, message);

    // CRITICAL FIX: Network/timeout errors should NOT be marked as auth failure
    const requiresAuth = meta?.requiresAuth ?? true;
    const isAuthExpired = Boolean(
      !isNetworkError && // Network errors are NOT auth failures
      !isTimeout &&      // Timeouts are NOT auth failures
      requiresAuth && 
      (status === 401 || status === 419)
    );

    const enhancedMessage = isTimeout
      ? `Request timed out after ${meta?.timeout || ENV.defaultTimeout}ms`
      : isNetworkError
        ? 'Network connection failed. Please check your internet connection.'
        : status === 429
          ? 'Too many requests. Please try again later.'
          : isAuthExpired
            ? 'Session expired. Please log in again.'
            : message;

    return {
      ...base,
      message: enhancedMessage,
      code: isAuthExpired ? 'AUTH_EXPIRED' : code,
      status,
      isTransient,
      isNetworkError,
      isTimeout,
      isServerError,
      isClientError,
      isAuthExpired,
      durationMs: meta?.startedAt ? Date.now() - meta.startedAt : undefined,
      raw: {
        axiosError: {
          code: error.code,
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        },
      },
    };
  }

  private static isTransientError(status?: number, code?: string, message?: string): boolean {
    // Network errors are transient
    const transientNetworkCodes = new Set([
      'ECONNRESET',
      'ETIMEDOUT',
      'EAI_AGAIN', // DNS error
      'ENETUNREACH',
      'ECONNREFUSED',
      'EPIPE',
      'ECONNABORTED',
      'ENOTFOUND', // DNS lookup failed
    ]);

    if (code && transientNetworkCodes.has(code)) return true;

    // Timeout and server errors are transient
    const transientStatusCodes = new Set([408, 425, 429, 500, 502, 503, 504]);
    if (status && transientStatusCodes.has(status)) return true;

    // Timeout messages indicate transient errors
    if (message && /timeout|timed out|exceeded|took too long/i.test(message)) return true;

    return false;
  }
}

/* =============================================================================
 * RETRY STRATEGY
 * ============================================================================= */

class RetryStrategy {
  private config: RetryConfig;

  constructor() {
    this.config = {
      maxRetries: ENV.maxRetries,
      retryableMethods: new Set(['GET', 'HEAD', 'OPTIONS']),
      retryDelay: (attempt: number) => {
        const baseDelay = ENV.retryDelayBase * Math.pow(2, attempt - 1);
        const jitter = Math.random() * ENV.retryJitter;
        return baseDelay + jitter;
      },
      shouldRetry: (error: StandardizedError, attempt: number) => {
        if (attempt >= this.config.maxRetries) return false;
        if (!error.isTransient) return false;

        // NEVER retry auth-expired
        if (error.isAuthExpired) return false;

        const method = error.method?.toUpperCase();
        if (method && !this.config.retryableMethods.has(method)) {
          // allow retry for network/timeouts on any method
          if ((error.isNetworkError || error.isTimeout)) return true;
          return false;
        }
        return true;
      },
    };
  }

  async executeWithRetry<T>(requestFn: () => Promise<T>, meta: RequestMetaInternal): Promise<T> {
    let lastError: StandardizedError | undefined;

    for (let attempt = 1; attempt <= this.config.maxRetries + 1; attempt++) {
      try {
        meta.attempt = attempt;
        return await requestFn();
      } catch (error) {
        const normalized = ErrorNormalizer.normalize(error, meta);
        lastError = normalized;

        if (!this.config.shouldRetry(normalized, attempt)) break;

        const delay = this.config.retryDelay(attempt);
        await new Promise((r) => setTimeout(r, delay));

        devLog('warn', `🔄 Retry ${attempt}/${this.config.maxRetries}`, {
          requestId: meta.requestId,
          url: meta.url,
          method: meta.method,
          delay: `${delay}ms`,
          error: normalized.message,
          code: normalized.code,
          status: normalized.status,
        });
      }
    }

    throw lastError ?? ErrorNormalizer.normalize(new Error('Unknown failure'), meta);
  }
}

/* =============================================================================
 * CIRCUIT BREAKER
 * ============================================================================= */

class CircuitBreaker {
  private states: Map<string, CircuitBreakerState>;
  constructor() {
    this.states = new Map();
  }

  canRequest(endpoint: string): boolean {
    const state = this.states.get(endpoint) || {
      failures: 0,
      lastFailure: 0,
      state: 'CLOSED' as const,
    };

    if (state.state === 'OPEN' && Date.now() - state.lastFailure > ENV.circuitBreakerResetTimeout) {
      state.state = 'HALF_OPEN';
    }

    return state.state !== 'OPEN';
  }

  recordSuccess(endpoint: string): void {
    const state = this.states.get(endpoint);
    if (state) {
      state.failures = 0;
      state.state = 'CLOSED';
    }
  }

  recordFailure(endpoint: string): void {
    let state = this.states.get(endpoint);
    if (!state) {
      state = { failures: 0, lastFailure: 0, state: 'CLOSED' };
    }

    state.failures++;
    state.lastFailure = Date.now();

    if (state.failures >= ENV.circuitBreakerThreshold) {
      state.state = 'OPEN';
    } else if (state.failures >= Math.floor(ENV.circuitBreakerThreshold / 2)) {
      state.state = 'HALF_OPEN';
    }

    this.states.set(endpoint, state);

    if (DEV && state.state === 'OPEN') {
      devLog('error', `⚡ Circuit breaker OPEN`, {
        endpoint,
        failures: state.failures,
        lastFailure: new Date(state.lastFailure).toISOString(),
        resetIn: `${ENV.circuitBreakerResetTimeout}ms`,
      });
    }
  }

  getState(endpoint: string) {
    return this.states.get(endpoint);
  }

  reset(endpoint: string) {
    const state = this.states.get(endpoint);
    if (state) {
      state.failures = 0;
      state.state = 'CLOSED';
    }
  }
}

/* =============================================================================
 * HTTP AGENTS
 * ============================================================================= */

const httpAgent = new http.Agent({
  keepAlive: true,
  maxSockets: ENV.maxSockets,
  timeout: ENV.socketTimeout,
});

const httpsAgent = new https.Agent({
  keepAlive: true,
  maxSockets: ENV.maxSockets,
  timeout: ENV.socketTimeout,
});

/* =============================================================================
 * MAIN API CLIENT
 * ============================================================================= */

export class ServerApiClient {
  private readonly client: AxiosInstance;
  private readonly retryStrategy: RetryStrategy;
  private readonly circuitBreaker: CircuitBreaker;
  private readonly refreshLock: RefreshLockManager;

  private baseURL: string;

  constructor() {
    this.baseURL = this.normalizeBaseURL(
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://api.indemenu.com/api/v1'
    );

    this.retryStrategy = new RetryStrategy();
    this.circuitBreaker = new CircuitBreaker();
    this.refreshLock = RefreshLockManager.getInstance();

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: ENV.defaultTimeout,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Client-Platform': 'web',
        'X-Client-Version': CLIENT_VERSION,
        'User-Agent': `IndeMenu-Server/${CLIENT_VERSION}`,
      },
      httpAgent,
      httpsAgent,
      validateStatus: () => true,
      maxContentLength: 10 * 1024 * 1024,
      maxBodyLength: 10 * 1024 * 1024,
      transitional: { clarifyTimeoutError: true },
    });

    this.attachInterceptors();

    devLog('info', '✅ Initialized', {
      baseURL: this.baseURL,
      defaultTimeout: `${ENV.defaultTimeout}ms`,
      env: process.env.NODE_ENV,
      maxRetries: ENV.maxRetries,
    });
  }

  private normalizeBaseURL(url: string): string {
    const trimmed = url.trim().replace(/\/+$/, '');
    try {
      // FIX: Replace url.parse() with new URL() to avoid DEP0169 warning
      new URL(trimmed);
      return trimmed;
    } catch {
      devLog('warn', 'Invalid base URL, using default', { provided: url });
      return 'http://api.indemenu.com/api/v1';
    }
  }

  private attachInterceptors(): void {
    this.client.interceptors.request.use(
      this.requestInterceptor.bind(this),
      this.requestErrorInterceptor.bind(this)
    );
    this.client.interceptors.response.use(
      this.responseInterceptor.bind(this),
      this.responseErrorInterceptor.bind(this)
    );
  }

  private async requestInterceptor(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
    const timer = new Timer();

    const userMeta = (config as AxiosRequestConfig).meta;
    const requestId = userMeta?.requestId || RequestIdGenerator.generate();
    const startedAt = Date.now();

    const classification = RouteClassifier.classify(config.url, config.method);
    config.timeout = classification.timeout;

    const endpoint = config.url || '/';
    if (!this.circuitBreaker.canRequest(endpoint)) {
      throw new Error(`Circuit breaker is open for ${endpoint}`);
    }

    const headers = config.headers || (config.headers = new AxiosHeaders());

    const requiresAuth = userMeta?.requiresAuth !== undefined ? Boolean(userMeta.requiresAuth) : true;

    headers.set('X-Request-ID', requestId);
    headers.set('X-Request-Timestamp', startedAt.toString());
    headers.set('X-Request-From', 'nextjs-server');
    headers.set('X-Request-Category', classification.category);

    // ✅ FIX: No proactive refresh - just attach auth if available
    let hasAuthHeader = false;
    if (requiresAuth) {
      try {
        const store = await cookies();
        const accessToken = store.get('access_token')?.value;

        if (accessToken) {
          headers.set('Authorization', `Bearer ${accessToken}`);
          hasAuthHeader = true;
        }
      } catch {
        // ignore cookie read failures
      }
    } else {
      // ensure no accidental auth header leakage
      try {
        headers.delete('Authorization');
      } catch {
        // ignore
      }
    }

    const meta: RequestMetaInternal = {
      requestId,
      startedAt,
      method: config.method?.toUpperCase() || 'GET',
      url: config.url || '',
      timeout: config.timeout ?? ENV.defaultTimeout,
      attempt: 0,
      routeCategory: classification.category,
      endpoint,
      requiresAuth,
      label: userMeta?.label,
    };

    (config as any).__meta = meta;

    devLog('debug', '📤 Request', {
      requestId,
      method: meta.method,
      url: meta.url,
      timeout: `${meta.timeout}ms`,
      category: meta.routeCategory,
      requiresAuth,
      hasAuthHeader,
      label: meta.label,
      processingTime: `${timer.elapsedMs()}ms`,
    });

    return config;
  }

  private requestErrorInterceptor(error: unknown): Promise<never> {
    const normalized = ErrorNormalizer.normalize(error);
    devLog('error', '❌ Request preparation failed', {
      error: normalized.message,
      code: normalized.code,
    });
    return Promise.reject(normalized);
  }

  private responseInterceptor(response: AxiosResponse): AxiosResponse {
    const meta: RequestMetaInternal | undefined = (response.config as any).__meta;
    const duration = meta?.startedAt ? Date.now() - meta.startedAt : undefined;

    if (meta?.endpoint) {
      if (response.status >= 500) this.circuitBreaker.recordFailure(meta.endpoint);
      else this.circuitBreaker.recordSuccess(meta.endpoint);
    }

    devLog('debug', '📥 Response', {
      requestId: meta?.requestId,
      method: meta?.method,
      url: response.config.url,
      status: response.status,
      duration: duration ? `${duration}ms` : undefined,
      requiresAuth: meta?.requiresAuth,
      label: meta?.label,
    });

    return response;
  }

  private async responseErrorInterceptor(error: AxiosError): Promise<never> {
    const meta: RequestMetaInternal = (error.config as any)?.__meta || ({
      requestId: RequestIdGenerator.generate(),
      startedAt: Date.now(),
      method: (error.config?.method || 'GET').toUpperCase(),
      url: error.config?.url || '',
      timeout: ENV.defaultTimeout,
      attempt: 1,
      endpoint: error.config?.url || '/',
      routeCategory: 'unknown',
      requiresAuth: true,
    } as RequestMetaInternal);

    const normalized = ErrorNormalizer.normalize(error, meta);

    if (meta.endpoint && normalized.isTransient) {
      this.circuitBreaker.recordFailure(meta.endpoint);
    }

    devLog(normalized.isTransient ? 'warn' : 'error', '❌ Response error', {
      requestId: meta.requestId,
      url: meta.url,
      method: meta.method,
      status: normalized.status,
      code: normalized.code,
      isTransient: normalized.isTransient,
      isTimeout: normalized.isTimeout,
      isNetworkError: normalized.isNetworkError,
      isAuthExpired: normalized.isAuthExpired,
      message: normalized.message,
      requiresAuth: meta.requiresAuth,
      label: meta.label,
    });

    // ✅ FIX: Only refresh on 401/419 when requiresAuth = true
    if (normalized.isAuthExpired && meta.requiresAuth) {
      return this.handleAuthExpired(error, meta);
    }

    return Promise.reject(normalized);
  }

  /**
   * Handle authentication expired error with single-flight refresh pattern
   */
  private async handleAuthExpired(error: AxiosError, meta: RequestMetaInternal): Promise<never> {
    devLog('warn', '🔐 Auth expired, attempting refresh', {
      requestId: meta.requestId,
      url: meta.url,
    });

    try {
      // Get refresh lock - either perform refresh or wait for existing one
      const shouldPerformRefresh = await this.refreshLock.acquireLock();
      
      let newAccessToken: string | null = null;
      
      if (shouldPerformRefresh === null) {
        // We should perform the refresh
        newAccessToken = await this.performTokenRefresh();
        this.refreshLock.releaseLock({ token: newAccessToken });
      } else {
        // Wait for existing refresh to complete
        newAccessToken = await shouldPerformRefresh;
      }

      if (newAccessToken) {
        // ✅ FIX: Retry original request with new token
        devLog('info', '🔄 Retrying original request with new token', {
          requestId: meta.requestId,
          url: meta.url,
        });

        // Update the original request config with new token
        if (error.config) {
          error.config.headers.set('Authorization', `Bearer ${newAccessToken}`);

          // Retry the original request
          const retryResponse = await this.client.request(error.config);
          
          // If successful, return the response (cast to never since this function returns Promise<never>)
          if (retryResponse.status < 300) {
            return retryResponse as never;
          }
        }
      }

      // If refresh failed or retry failed, reject with original error
      return Promise.reject(ErrorNormalizer.normalize(error, meta));
    } catch (refreshError) {
      // Ensure lock is released even on error
      this.refreshLock.forceRelease();
      
      devLog('error', '❌ Refresh failed', {
        requestId: meta.requestId,
        error: refreshError instanceof Error ? refreshError.message : String(refreshError),
      });
      
      return Promise.reject(ErrorNormalizer.normalize(error, meta));
    }
  }

  /**
   * Perform actual token refresh
   */
  private async performTokenRefresh(): Promise<string | null> {
    try {
      const store = await cookies();
      const refreshToken = store.get('refresh_token')?.value;

      if (!refreshToken) {
        devLog('warn', 'No refresh token available');
        return null;
      }

      devLog('info', '🔄 Performing token refresh');
      
      // Create a fresh axios instance for refresh to avoid interceptors
      const refreshClient = axios.create({
        baseURL: this.baseURL,
        timeout: ENV.authRefreshTimeout,
        headers: {
          'Content-Type': 'application/json',
          'X-Request-From': 'nextjs-server-refresh',
        },
      });

      const response = await refreshClient.post<{
        auth?: {
          access_token?: string;
          refresh_token?: string;
        };
      }>(
        '/auth/refresh-token',
        { refresh_token: refreshToken },
        { timeout: ENV.authRefreshTimeout }
      );

      const newAccessToken = response.data?.auth?.access_token;
      const newRefreshToken = response.data?.auth?.refresh_token;

      if (newAccessToken) {
        // Update cookies with new tokens
        // Note: In server components, we need to use appropriate cookie methods
        // This is a simplified version - adjust based on your cookie management
        devLog('info', '✅ Refresh successful', {
          newTokenLength: newAccessToken.length,
        });

        return newAccessToken;
      }

      return null;
    } catch (error) {
      devLog('error', '❌ Token refresh failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /* ===========================================================================
   * PUBLIC API METHODS
   * =========================================================================== */

  async request<T = unknown>(config: AxiosRequestConfig): Promise<ApiSuccessResponse<T>> {
    const meta = this.createRequestMeta(config);

    return this.retryStrategy.executeWithRetry(async () => {
      const response = await this.client.request(config);

      if (response.status >= 300) {
        const httpErr = ErrorNormalizer.normalizeHttpResponseAsError(response, meta);

        if (meta.endpoint && httpErr.isTransient) {
          this.circuitBreaker.recordFailure(meta.endpoint);
        }

        throw httpErr;
      }

      const data = response.data;

      if (data && typeof data === 'object' && 'status' in data) {
        return { ...(data as any), requestId: meta.requestId } as ApiSuccessResponse<T>;
      }

      return {
        status: 'success',
        data: data as T,
        timestamp: new Date().toISOString(),
        requestId: meta.requestId,
      };
    }, meta);
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ method: 'GET', url, ...(config || {}) });
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ method: 'POST', url, data, ...(config || {}) });
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ method: 'PUT', url, data, ...(config || {}) });
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ method: 'PATCH', url, data, ...(config || {}) });
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ method: 'DELETE', url, ...(config || {}) });
  }

  /* ===========================================================================
   * UTILITY METHODS
   * =========================================================================== */

  private createRequestMeta(config: AxiosRequestConfig): RequestMetaInternal {
    const requestId = config.meta?.requestId || RequestIdGenerator.generate();
    const classification = RouteClassifier.classify(config.url, config.method);

    const requiresAuth = config.meta?.requiresAuth !== undefined ? Boolean(config.meta.requiresAuth) : true;

    return {
      requestId,
      startedAt: Date.now(),
      method: (config.method || 'GET').toUpperCase(),
      url: config.url || '',
      timeout: classification.timeout,
      attempt: 0,
      routeCategory: classification.category,
      endpoint: config.url || '/',
      requiresAuth,
      label: config.meta?.label,
    };
  }

  // debug helpers
  getCircuitBreakerState(endpoint: string) {
    return this.circuitBreaker.getState(endpoint);
  }

  resetCircuitBreaker(endpoint: string) {
    this.circuitBreaker.reset(endpoint);
  }
}

/* =============================================================================
 * GLOBAL INSTANCE & EXPORTS
 * ============================================================================= */

let globalInstance: ServerApiClient | null = null;

export function getServerApiClient(): ServerApiClient {
  if (!globalInstance) globalInstance = new ServerApiClient();
  return globalInstance;
}

export const serverApiClient = getServerApiClient();
export const serverApi = serverApiClient;
export default serverApiClient;